import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  Users,
  Building,
  FileText,
  Calculator,
  BarChart3,
  Settings,
  HelpCircle,
  ChevronLeft,
  LogOut,
  X,
  ChevronRight,
  UserCheck,
  TrendingUp,
  Palette,
  UsersRound,
  Sun,
  Moon,
  Monitor,
} from 'lucide-react'
import { useAuthStore } from '../../store/authStore'
import { useTheme } from '../../contexts/ThemeContext'

interface PartnerSidebarProps {
  isOpen: boolean
  onToggle: () => void
  isMobile?: boolean
  onNavigate?: () => void
}

export function PartnerSidebar({ isOpen, onToggle, isMobile = false, onNavigate }: PartnerSidebarProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { logout, user, partner } = useAuthStore()
  const { theme, setTheme, effectiveTheme } = useTheme()
  const [activeItem, setActiveItem] = useState('dashboard')

  useEffect(() => {
    const path = location.pathname.substring(1) || 'dashboard'
    setActiveItem(path)
  }, [location])

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const handleNavigation = (itemId: string, path: string) => {
    setActiveItem(itemId)
    navigate(path)
    if (onNavigate) {
      onNavigate()
    }
  }

  const menuSections = [
    {
      title: 'PRINCIPAL',
      items: [
        { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
        { id: 'clients', icon: Building, label: 'Clínicas', path: '/clients' },
        { id: 'team', icon: UsersRound, label: 'Equipe', path: '/team' },
      ]
    },
    {
      title: 'OPERACOES',
      items: [
        { id: 'tax-management', icon: Calculator, label: 'Gestao Fiscal', path: '/tax-management' },
        { id: 'documents', icon: FileText, label: 'Documentos', path: '/documents' },
        // Pós-MVP: Relatórios Avançados - schema do banco incompatível
        // { id: 'reports', icon: BarChart3, label: 'Relatorios', path: '/reports' },
        { id: 'analytics', icon: TrendingUp, label: 'Analytics', path: '/analytics' },
      ]
    },
    {
      title: 'SUPORTE',
      items: [
        { id: 'support', icon: HelpCircle, label: 'Suporte', path: '/support' },
      ]
    },
    {
      title: 'CONFIGURACOES',
      items: [
        { id: 'whitelabel', icon: Palette, label: 'Whitelabel', path: '/whitelabel' },
        { id: 'settings', icon: Settings, label: 'Configuracoes', path: '/settings' },
      ]
    }
  ]

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  if (isMobile && !isOpen) {
    return null
  }

  return (
    <>
      {/* Mobile overlay */}
      {isMobile && isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed left-0 top-0 bottom-0 z-50
          bg-white dark:bg-gray-900
          border-r border-gray-200 dark:border-gray-800
          flex flex-col
          transition-all duration-300 ease-in-out
          ${isMobile ? 'w-72' : isOpen ? 'w-64' : 'w-[72px]'}
          ${isMobile && !isOpen ? '-translate-x-full' : 'translate-x-0'}
        `}
      >
        {/* Logo Header */}
        <div className={`
          py-5 flex items-center border-b border-gray-200 dark:border-gray-800
          ${isOpen || isMobile ? 'px-5 justify-between' : 'px-3 justify-center'}
        `}>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/25">
              <Building className="w-6 h-6 text-white" />
            </div>
            {(isOpen || isMobile) && (
              <div className="flex flex-col">
                <span className="font-bold text-gray-900 dark:text-white text-base leading-tight">
                  {partner?.name || 'Contabilidade'}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  NGC Medical Partner
                </span>
              </div>
            )}
          </div>
          {isMobile && (
            <button
              onClick={onToggle}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {menuSections.map((section, sectionIndex) => (
            <div key={section.title} className={sectionIndex > 0 ? 'mt-6' : ''}>
              {(isOpen || isMobile) && (
                <div className="px-3 mb-2">
                  <span className="text-[11px] font-semibold text-gray-400 dark:text-gray-400 tracking-wider">
                    {section.title}
                  </span>
                </div>
              )}
              <div className="space-y-1">
                {section.items.map((item) => {
                  const isActive = activeItem === item.id
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleNavigation(item.id, item.path)}
                      title={!isOpen && !isMobile ? item.label : undefined}
                      className={`
                        w-full flex items-center gap-3 rounded-xl transition-all duration-200
                        ${isOpen || isMobile ? 'px-3 py-2.5' : 'px-0 py-2.5 justify-center'}
                        ${isActive
                          ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                        }
                      `}
                    >
                      <item.icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-white' : ''}`} />
                      {(isOpen || isMobile) && (
                        <span className="text-sm font-medium truncate">{item.label}</span>
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Bottom Section */}
        <div className="border-t border-gray-200 dark:border-gray-800 p-3">
          {/* Theme Toggle */}
          <div className={`mb-3 ${isOpen || isMobile ? '' : 'flex justify-center'}`}>
            {(isOpen || isMobile) ? (
              <div className="p-1.5 rounded-xl bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-1">
                  {[
                    { key: 'light', icon: Sun },
                    { key: 'dark', icon: Moon },
                    { key: 'system', icon: Monitor },
                  ].map(({ key, icon: Icon }) => (
                    <button
                      key={key}
                      onClick={() => setTheme(key as 'light' | 'dark' | 'system')}
                      title={key === 'light' ? 'Claro' : key === 'dark' ? 'Escuro' : 'Auto'}
                      className={`flex-1 flex items-center justify-center p-2.5 rounded-lg transition-all duration-200 ${
                        theme === key
                          ? 'bg-blue-500 text-white shadow-md'
                          : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-white dark:hover:bg-gray-700'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <button
                onClick={() => setTheme(effectiveTheme === 'dark' ? 'light' : 'dark')}
                title={effectiveTheme === 'dark' ? 'Modo Claro' : 'Modo Escuro'}
                className="w-11 h-11 flex items-center justify-center rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-blue-500 hover:text-white transition-all duration-200"
              >
                {effectiveTheme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
            )}
          </div>

          {/* User Profile */}
          {(isOpen || isMobile) && (
            <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50 mb-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-sm font-semibold">
                  {user?.name ? getInitials(user.name) : 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                    {user?.name || 'Usuario'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {user?.email || 'email@example.com'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            title={!isOpen && !isMobile ? 'Sair' : undefined}
            className={`
              w-full flex items-center gap-3 rounded-xl transition-all duration-200
              text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10
              ${isOpen || isMobile ? 'px-3 py-2.5' : 'px-0 py-2.5 justify-center'}
            `}
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {(isOpen || isMobile) && (
              <span className="text-sm font-medium">Sair</span>
            )}
          </button>
        </div>

        {/* Toggle Button - Desktop only */}
        {!isMobile && (
          <button
            onClick={onToggle}
            className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-md flex items-center justify-center hover:scale-110 transition-transform"
          >
            {isOpen ? (
              <ChevronLeft className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            )}
          </button>
        )}
      </aside>
    </>
  )
}
